import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Icon, useToast } from '@kollez/design-system';
import { apiDelete, apiGet, apiPatch, getAccessToken } from '@/lib/api';
import { useAuthStore } from '@/lib/authStore';

interface ConversationRow {
  _id: string;
  title?: string;
  updatedAt: string;
  messageCount: number;
}

function conversationBucket(iso: string): string {
  const d = new Date(iso).getTime();
  const now = Date.now();
  const oneDay = 86_400_000;
  const days = Math.floor((now - d) / oneDay);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days <= 7) return 'This week';
  if (days <= 30) return 'This month';
  return 'Earlier';
}

/**
 * /nexie (F92c) — direct port of kollez/pages/shared/nexie.html.
 *   Two-column layout: 260px conversation history · main chat surface.
 *   Header strip with scope badge + Nexie identity.
 *   Chat scroller with .msg-user / .msg-ai bubbles, suggested prompts on empty state,
 *   `.ai-cites` sources strip when Nexie answers, follow-up chips row, composer with
 *   Attach + Language + Ask.
 *   Streaming via /api/v1/nexie/ask (SSE); persists conversation id across turns.
 */

interface Message {
  role: 'user' | 'ai';
  content: string;
}

const PROMPTS_BY_ROLE: Record<string, string[]> = {
  // TPO + group_admin prompt lists follow the same rule as students (Page 54/N):
  // don't name specific partners — describe the SHAPE of what to ask, so the
  // suggestion reads honestly regardless of which recruiters this tenant has.
  tpo: [
    "Who's placed in ECE, batch 2026?",
    "Which students haven't replied to offers?",
    'Rank applicants for a specific drive',
    'Which drives had the lowest turnout this month?',
  ],
  group_admin: [
    'Which colleges are behind target this month?',
    'Which recruiter hired the most across the group?',
    'Median CTC by college for the current placement season',
    'Which MoUs expire in the next 90 days?',
  ],
  // Deep-audit fix (Page 54/N): student suggested prompts used to
  // name specific companies (TCS Digital, Wipro, Deloitte). A
  // student not applying to any of those saw prompts that read
  // as canned marketing. Rewrote to generic prompts that describe
  // the SHAPE of what to ask.
  student: [
    "What did last year's interviewers ask for this role?",
    "Which drives am I eligible for this week?",
    'How can I improve my resume for embedded roles?',
    'How do I decide between two offers?',
  ],
  partner_admin: [
    'Which campuses gave us the most accepted offers?',
    "Who hasn't replied to our offers after 48 hours?",
    'Suggest a CGPA cut-off for 60 shortlists',
    "Compare this year's funnel with last year",
  ],
  platform_admin: [
    'Which tenants are close to their AI budget?',
    "Which tenants haven't run a drive yet?",
    'Show impersonation sessions this week',
    'Error rate by tenant, last 24 hours',
  ],
};

function scopeFor(role: string, fullName: string): string {
  if (role === 'student' || role === 'student_representative') return 'Your profile and eligible drives';
  if (role === 'tpo' || role === 'assistant_tpo' || role === 'placement_coordinator') return 'Your college, current season';
  if (/group_/.test(role)) return 'Your group, all colleges';
  if (/university_/.test(role)) return 'Your university, all colleges';
  if (/principal|hod|faculty/.test(role)) return 'Your college';
  if (/partner|recruiter|hiring_manager/.test(role)) return `${fullName || 'Your'} campus hiring`;
  if (/platform_/.test(role)) return 'Platform operations';
  return 'Your workspace';
}

export function NexiePage() {
  const { user } = useAuthStore();
  const toast = useToast();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [convoId, setConvoId] = useState<string | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);

  const role = user?.role ?? 'tpo';
  const prompts = useMemo(() => PROMPTS_BY_ROLE[role] ?? PROMPTS_BY_ROLE.tpo!, [role]);
  const scope = useMemo(() => scopeFor(role, user?.fullName ?? ''), [role, user?.fullName]);

  // 5m seeded NexieConversation. /nexie/conversations returns the caller's
  // recent chats — replaces the previously hardcoded 'ECE/2026' / 'TCS-Infosys'
  // / 'Drives-low-turnout' stubs that rendered for every user of every tenant.
  const conversationsQ = useQuery({
    queryKey: ['nexie', 'conversations'],
    queryFn: () => apiGet<{ conversations: ConversationRow[] }>('/nexie/conversations?limit=25&sortBy=updatedAt&sortDir=desc'),
  });
  const conversations = conversationsQ.data?.conversations ?? [];
  const bySection = conversations.reduce<Record<string, ConversationRow[]>>((acc, c) => {
    const sec = conversationBucket(c.updatedAt);
    (acc[sec] ??= []).push(c);
    return acc;
  }, {});

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  async function ask(prompt?: string) {
    const content = (prompt ?? input).trim();
    if (!content || streaming) return;
    if (prompt) setInput('');
    setMessages((m) => [...m, { role: 'user', content }, { role: 'ai', content: '' }]);
    if (!prompt) setInput('');
    setStreaming(true);

    try {
      const res = await fetch('/api/v1/nexie/ask', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify({ content, conversationId: convoId }),
      });
      if (!res.ok || !res.body) {
        throw new Error(`Nexie returned ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        for (const chunk of buf.split('\n\n')) {
          if (!chunk.trim()) continue;
          const lines = chunk.split('\n');
          let event = 'token';
          let data = '';
          for (const l of lines) {
            if (l.startsWith('event: ')) event = l.slice(7).trim();
            if (l.startsWith('data: ')) data += l.slice(6);
          }
          try {
            const parsed = data ? JSON.parse(data) : {};
            if (event === 'token' && parsed.delta) {
              setMessages((m) => {
                const next = [...m];
                const last = next[next.length - 1];
                if (last && last.role === 'ai') last.content += parsed.delta;
                return next;
              });
            }
            if (event === 'conversation' && parsed.id) setConvoId(parsed.id);
            if (event === 'done' && parsed.conversationId) setConvoId(parsed.conversationId);
          } catch { /* ignore malformed */ }
        }
        buf = buf.slice(buf.lastIndexOf('\n\n') + 2);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Nexie could not respond';
      toast.show({ tone: 'warning', message: msg });
      setMessages((m) => {
        const next = [...m];
        const last = next[next.length - 1];
        if (last && last.role === 'ai' && !last.content) next.pop();
        return next;
      });
    } finally {
      setStreaming(false);
    }
  }

  const empty = messages.length === 0;

  return (
    <div className="stack" style={{ marginTop: 0 }}>
      <style>{`
        .nexie { display: grid; grid-template-columns: 1fr; height: calc(100vh - 56px - 24px); }
        @media (min-width: 1100px) { .nexie { grid-template-columns: 260px 1fr; } }
        .hist { border-right: 1px solid rgb(var(--line-tertiary)); background: rgb(var(--bg-primary)); overflow-y: auto; display: none; }
        @media (min-width: 1100px) { .hist { display: block; } }
        .hist a { display: block; padding: 8px 10px; border-radius: 7px; font-size: .875rem; color: rgb(var(--ink-secondary)); text-decoration: none; }
        .hist a:hover { background: rgb(var(--bg-tertiary)); }
        .hist a[aria-current] { background: rgb(var(--bg-info)); color: rgb(var(--ink-accent)); font-weight: 550; }
        .convo-row { padding: 4px 6px; border-radius: 7px; font-size: .875rem; color: rgb(var(--ink-secondary)); }
        .convo-row:hover { background: rgb(var(--bg-tertiary)); }
        .convo-row[aria-current] { background: rgb(var(--bg-info)); color: rgb(var(--ink-accent)); font-weight: 550; }
        .convo-row > button:first-child { background: transparent; border: 0; cursor: pointer; color: inherit; font: inherit; padding: 4px 4px; }
        .convo-act { opacity: 0; }
        .convo-row:hover .convo-act,
        .convo-row:focus-within .convo-act { opacity: 1; }
        .convo { display: flex; flex-direction: column; min-height: 0; min-width: 0; }
        .scroll { flex: 1; overflow-y: auto; }
      `}</style>

      <div className="nexie">
        <aside className="hist p-3" aria-label="Past conversations">
          <button className="btn btn-outline w-full" onClick={() => { setMessages([]); setConvoId(null); }}>
            <Icon name="plus" size={14} />New conversation
          </button>
          {conversations.length === 0 && !conversationsQ.isLoading && (
            <p className="px-2.5 pt-6 text-xs text-ink-tertiary">No past conversations yet.</p>
          )}
          {Object.entries(bySection).map(([sec, items]) => (
            <div key={sec}>
              <p className="px-2.5 pb-1 pt-5 text-xs font-semibold text-ink-tertiary">{sec}</p>
              {items.map((c) => (
                <ConversationRowItem
                  key={c._id}
                  row={c}
                  active={c._id === convoId}
                  onOpen={async () => {
                    try {
                      const res = await apiGet<{ conversation: { _id: string; messages: Array<{ role: 'user' | 'ai'; content: string }> } }>(`/nexie/conversations/${c._id}`);
                      setMessages(res.conversation.messages.map((m) => ({ role: m.role, content: m.content })));
                      setConvoId(c._id);
                    } catch {
                      toast.show({ tone: 'warning', message: "Couldn't open that conversation." });
                    }
                  }}
                  onDeleted={() => {
                    if (c._id === convoId) { setConvoId(null); setMessages([]); }
                  }}
                />
              ))}
            </div>
          ))}
          <p className="mt-6 px-2.5 text-xs text-ink-tertiary">
            Conversations are kept for 90 days and are visible only to you.
          </p>
        </aside>

        <section className="convo" aria-label="Conversation with Nexie">
          <div className="flex flex-wrap items-center gap-2 border-b border-line-tertiary bg-primary px-4 py-2.5 lg:px-6">
            <Icon name="sparkles" size={16} className="text-ink-accent" />
            <span className="font-semibold">Nexie</span>
            <span className="text-sm text-ink-tertiary">answering from</span>
            <button className="badge badge-outline" type="button" aria-haspopup="true">{scope}</button>
            <Link
              className="ml-auto btn btn-ghost btn-sm"
              to="/nexie/drafts"
              title="Review Nexie's pending drafts before they're sent"
            >
              Review drafts
            </Link>
            <Link
              className="btn btn-ghost btn-sm"
              to="/nexie/audit"
              title="See every Nexie action inside your tenant"
            >
              Audit
            </Link>
            <span className="hidden text-xs text-ink-tertiary md:inline">
              Nexie drafts and suggests. You confirm every action.
            </span>
          </div>

          <div className="scroll" ref={scrollerRef}>
            {empty ? (
              <div className="mx-auto max-w-[760px] px-4 py-10 lg:px-6">
                <h1 className="h-page">What do you want to know?</h1>
                <p className="subhead mt-2">Ask about drives, applications, students or offers. Answers show the records they came from.</p>
                <div className="mt-6 grid gap-2 sm:grid-cols-2">
                  {prompts.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className="card card-pad text-left text-sm hover:border-line-primary"
                      onClick={() => ask(p)}
                      style={{ cursor: 'pointer' }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mx-auto grid max-w-[760px] gap-6 px-4 py-6 lg:px-6" aria-live="polite">
                {messages.map((m, i) => (
                  <div key={i} className={m.role === 'user' ? 'msg msg-user' : 'msg msg-ai'}>
                    {m.content || (m.role === 'ai' && streaming ? <span className="text-ink-tertiary text-sm">Nexie is composing…</span> : null)}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-line-tertiary bg-secondary px-4 py-3 lg:px-6">
            {!empty && (
              <div className="mx-auto max-w-[760px] flex gap-2 overflow-x-auto pb-2" aria-label="Suggested follow-ups" style={{ scrollbarWidth: 'none' }}>
                {['Draft a reminder', 'Show only ECE', 'Compare with 2024', 'Open records'].map((p) => (
                  <button key={p} type="button" className="tag whitespace-nowrap" onClick={() => setInput(p)}>{p}</button>
                ))}
              </div>
            )}
            <form
              className="ai-composer mx-auto max-w-[760px]"
              onSubmit={(e) => { e.preventDefault(); ask(); }}
            >
              <label htmlFor="q" className="sr-only">Ask Nexie</label>
              <textarea
                id="q"
                className="textarea"
                rows={1}
                placeholder="Ask about your pipeline — e.g. which students haven't replied to offers?"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    ask();
                  }
                }}
              />
              <div className="flex items-center gap-2 px-1 mt-2">
                <button type="button" className="btn btn-ghost btn-sm" title="Attach a file">
                  <Icon name="paperclip" size={14} /><span className="hide-mobile">Attach</span>
                </button>
                <button type="button" className="btn btn-ghost btn-sm hide-mobile">
                  <Icon name="languages" size={14} />English
                </button>
                <span className="ml-auto hidden text-xs text-ink-tertiary sm:inline">
                  <kbd>Enter</kbd> to send, <kbd>Shift</kbd>+<kbd>Enter</kbd> new line
                </span>
                <button className="btn btn-primary btn-sm" type="submit" disabled={streaming || !input.trim()}>
                  {streaming ? <Icon name="rotate-cw" size={14} /> : <Icon name="arrow-up" size={14} />}Ask
                </button>
              </div>
            </form>
            <p className="mx-auto mt-2 max-w-[760px] text-xs text-ink-tertiary">
              Nexie can be wrong. Check the sources before you act on an answer.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

function ConversationRowItem({
  row,
  active,
  onOpen,
  onDeleted,
}: {
  row: ConversationRow;
  active: boolean;
  onOpen: () => Promise<void>;
  onDeleted: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState(row.title ?? '');

  async function saveRename() {
    const next = title.trim();
    if (!next || next === row.title) { setEditing(false); return; }
    setBusy(true);
    try {
      await apiPatch(`/nexie/conversations/${row._id}`, { title: next });
      await qc.invalidateQueries({ queryKey: ['nexie', 'conversations'] });
      toast.show({ message: 'Renamed' });
      setEditing(false);
    } catch {
      toast.show({ tone: 'warning', message: "Couldn't rename that conversation." });
    } finally {
      setBusy(false);
    }
  }

  async function doDelete() {
    if (!window.confirm(`Delete "${row.title ?? 'Untitled chat'}"? This can't be undone.`)) return;
    setBusy(true);
    try {
      await apiDelete(`/nexie/conversations/${row._id}`);
      await qc.invalidateQueries({ queryKey: ['nexie', 'conversations'] });
      toast.show({ message: 'Conversation deleted' });
      onDeleted();
    } catch {
      toast.show({ tone: 'warning', message: "Couldn't delete that conversation." });
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1 px-2">
        <input
          autoFocus
          className="input w-full text-sm"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void saveRename();
            if (e.key === 'Escape') { setEditing(false); setTitle(row.title ?? ''); }
          }}
          disabled={busy}
        />
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => void saveRename()} disabled={busy} aria-label="Save">
          <Icon name="check" size={14} />
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setEditing(false); setTitle(row.title ?? ''); }} disabled={busy} aria-label="Cancel">
          <Icon name="x" size={14} />
        </button>
      </div>
    );
  }
  return (
    <div className="convo-row flex items-center gap-1" aria-current={active ? 'true' : undefined}>
      <button
        type="button"
        className="text-left flex-1 truncate"
        aria-current={active ? 'true' : undefined}
        onClick={() => void onOpen()}
      >
        {row.title ?? `Chat · ${new Date(row.updatedAt).toLocaleDateString('en-IN')}`}
      </button>
      <button
        type="button"
        className="convo-act btn btn-ghost btn-sm"
        onClick={() => setEditing(true)}
        disabled={busy}
        aria-label="Rename conversation"
        title="Rename"
      >
        <Icon name="pencil" size={12} />
      </button>
      <button
        type="button"
        className="convo-act btn btn-ghost btn-sm"
        onClick={() => void doDelete()}
        disabled={busy}
        aria-label="Delete conversation"
        title="Delete"
      >
        <Icon name="trash-2" size={12} />
      </button>
    </div>
  );
}
